/**
 * User Account Settings Component
 *
 * Page de paramètres utilisateur combinant:
 * - Profil (informations personnelles, avatar, background)
 * - Sécurité (mot de passe, 2FA, appareils)
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Shield,
  ChevronLeft,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProfileSettings } from './ProfileSettings';
import { AccountSecurity } from './AccountSecurity';

type ActiveTab = 'profile' | 'security';

export interface UserAccountSettingsProps {
  /** Classe CSS additionnelle */
  className?: string;
  /** Tab initialement actif */
  initialTab?: ActiveTab;
}

export const UserAccountSettings: React.FC<UserAccountSettingsProps> = ({
  className = '',
  initialTab = 'profile',
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Determine initial tab from URL
  const getInitialTab = (): ActiveTab => {
    if (location.pathname.includes('/security')) return 'security';
    if (location.pathname.includes('/profile')) return 'profile';
    return initialTab;
  };

  const [activeTab, setActiveTab] = useState<ActiveTab>(getInitialTab);

  const tabs: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
    { id: 'profile', label: t('auth.account.tabProfile'), icon: <User className="w-5 h-5" /> },
    { id: 'security', label: t('auth.account.tabSecurity'), icon: <Shield className="w-5 h-5" /> },
  ];

  const cardClasses = `bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 p-2`;

  return (
    <div className={`min-h-screen ${className}`}>
      {/* Header with back button */}
      <div className="px-8 pt-8 pb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-teal-700 mb-4 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>{t('auth.account.back')}</span>
        </button>

        <h1 className="text-2xl font-bold text-gray-900">{t('auth.account.title')}</h1>
        <p className="text-gray-500 mt-1">
          {t('auth.account.subtitle')}
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="px-8 pb-6">
        <div className={cardClasses}>
          <nav className="flex gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-6 py-3 rounded-xl whitespace-nowrap
                  transition-all duration-300 flex-1 justify-center
                  ${
                    activeTab === tab.id
                      ? 'bg-white/60 text-teal-700 font-semibold shadow-lg border border-white/50'
                      : 'text-gray-600 hover:bg-white/30 hover:text-gray-900'
                  }
                `}
              >
                {tab.icon}
                <span className="text-sm">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="px-8 pb-12">
        {activeTab === 'profile' ? (
          <ProfileSettings />
        ) : (
          <AccountSecurity />
        )}
      </div>
    </div>
  );
};

export default UserAccountSettings;
